"use client";

import { useFormatter } from "use-intl";

import { ChevronRightIcon } from "@/components/appIcons";
import type { EntityAboutSection } from "@/components/catalog-entity/entity-about-section";
import { cn } from "@/lib/utils";

interface EntityAboutCardsProps {
  panelId: string;
  sections: EntityAboutSection[];
  onOpen: (key: string) => void;
}

/**
 * Compact entry points to the About panel: a swipeable row on mobile, a grid on
 * tablets and a stacked rail beside the header on wide screens. Previews are
 * real links so the most relevant targets stay visible and crawlable.
 */
export function EntityAboutCards({ panelId, sections, onOpen }: EntityAboutCardsProps) {
  const format = useFormatter();
  if (sections.length === 0) return null;

  return (
    <div
      className={cn(
        "-mx-4 flex snap-x snap-mandatory gap-2 overflow-x-auto px-4 pr-12 no-scrollbar",
        "[mask-image:linear-gradient(to_right,#000_calc(100%-3rem),transparent)]",
        "md:mx-0 md:grid md:grid-cols-3 md:overflow-visible md:px-0 md:[mask-image:none]",
        "xl:flex xl:flex-col",
      )}
    >
      {sections.map((section) => (
        <section
          key={section.key}
          className="w-64 shrink-0 snap-start rounded-xl border border-border bg-background/55 px-3.5 py-2.5 backdrop-blur-sm transition-colors hover:border-primary/40 md:w-auto"
        >
          <h2 className="text-sm font-bold">
            <button
              type="button"
              onClick={() => onOpen(section.key)}
              disabled={section.loading}
              aria-haspopup="dialog"
              aria-controls={panelId}
              className="flex min-h-8 w-full items-center gap-1.5 text-left max-sm:min-h-11 transition-colors hover:text-primary disabled:cursor-default disabled:hover:text-foreground"
            >
              {section.label}
              {!section.loading && (
                <span className="font-medium text-muted-foreground">
                  {format.number(section.count)}
                </span>
              )}
              <ChevronRightIcon className="ml-auto text-[14px] text-muted-foreground" />
            </button>
          </h2>
          <div className="mt-1 flex min-h-8 items-center gap-x-2.5 overflow-hidden whitespace-nowrap text-[13px]">
            {section.preview}
          </div>
        </section>
      ))}
    </div>
  );
}
